import { Controller } from "@hotwired/stimulus"
import { GridStack } from "gridstack"
import { submitAndReport } from "./flash_toasts"

// Wraps GridStack over a dashboard's own .grid-stack container (see
// organizations/dashboards/show.html.erb): the one drag/resize dependency in this app. Each
// .grid-stack-item's gs-x/gs-y/gs-w/gs-h attributes seed GridStack from DashboardWidget's own
// persisted grid_x/y/w/h columns; every drag or resize fires GridStack's own "change" event with
// the widgets that actually moved, which gets bulk-saved back to DashboardsController#reposition
// in one request rather than one per widget.
//
// editableValue is false for a viewer (see DashboardPolicy#update?): GridStack's own staticGrid
// option renders the identical grid with every drag/resize handle disabled, so a viewer sees
// exactly the same layout an admin does, just not able to change it.
// The saved layout is always a 12-column one; a phone's one-column view is derived from it and
// never saved (see syncColumn).
const FULL_COLUMNS = 12

export default class extends Controller {
  static values = { editable: Boolean, repositionUrl: String }

  connect() {
    this.grid = GridStack.init({
      staticGrid: !this.editableValue,
      column: FULL_COLUMNS,
      cellHeight: "80px"
      // float defaults to false: every widget auto-compacts upward to close whatever gap a drag
      // or a removed widget would otherwise leave, so the vertical space between rows stays the
      // margin GridStack itself puts there, not whatever arbitrary gap a widget happened to get
      // dropped at. This app shipped with float: true from the start (no widget count small
      // enough to notice); once a dashboard grew a handful of rows, that gap became genuinely
      // inconsistent from row to row, confirmed live by disabling float on an 11-widget dashboard
      // that had picked up gaps this way and watching every widget snap into a tight, even grid.
    }, this.element.querySelector(".grid-stack"))

    // One column below Bootstrap's md breakpoint: twelve columns on a phone left every widget
    // about 95px wide, too narrow for a title, a table label or a chart axis. Set after init
    // rather than passed to it: init at 12 columns first loads every widget's own saved 12-column
    // position, so GridStack caches that layout and restores it exactly when the window widens
    // again. Passing columnOpts to init on a phone loads the widgets straight into one column
    // instead, with no 12-column layout cached to go back to. breakpointForWindow measures the
    // viewport, the same width Bootstrap's md breakpoint is measured against, and "list" keeps
    // the widgets in their desktop reading order (left to right, then top to bottom).
    this.grid.updateOptions({
      columnOpts: { breakpointForWindow: true, columnMax: FULL_COLUMNS, breakpoints: [ { w: 767, c: 1 } ], layout: "list" }
    })
    this.currentColumn = this.grid.getColumn()
    this.syncEditable()
    // GridStack fires "resizecontent" after every width change it handles, column switch or not:
    // the one place to notice a switch that moved nothing (every widget already full width).
    this.grid.on("resizecontent", () => this.syncColumn())

    if (this.editableValue) {
      this.grid.on("change", () => {
        if (!this.syncColumn() && this.grid.getColumn() === FULL_COLUMNS) this.savePositions()
      })
      // See beginInstantScroll's own comment: GridStack's own auto-scroll-while-dragging-near-
      // the-edge logic breaks under this app's global smooth-scroll CSS, so it's suspended for
      // exactly the span of a drag or resize.
      this.grid.on("dragstart resizestart", () => this.beginInstantScroll())
      this.grid.on("dragstop resizestop", () => this.endInstantScroll())
    }
  }

  disconnect() {
    this.endInstantScroll()
    this.grid?.destroy(false)
  }

  // Returns true when GridStack's column count just changed. A column switch rewrites every
  // widget's gs-x/gs-w for the new count and fires "change" for it, which must never be saved:
  // saving the one-column phone view would overwrite the desktop layout, and switching back is
  // only GridStack restoring the layout it already had. The one-column view is read-only too,
  // since a drag there has no 12-column position to save.
  syncColumn() {
    const column = this.grid.getColumn()
    if (column === this.currentColumn) return false

    this.currentColumn = column
    this.syncEditable()
    return true
  }

  syncEditable() {
    this.grid.setStatic(!this.editableValue || this.grid.getColumn() !== FULL_COLUMNS)
  }

  // GridStack auto-scrolls the page while a drag/resize nears the viewport's top or bottom edge,
  // by writing the scrolling element's own scrollTop directly and, each animation frame, checking
  // whether that write actually changed anything to decide whether to keep going (see
  // dd-draggable.js's own _autoScrollTick). This app's global html { scroll-behavior: smooth }
  // (see application.bootstrap.scss, for anchor-link jumps) makes that same scrollTop write
  // animate instead of applying immediately, so GridStack's very next synchronous read of
  // scrollTop still sees the old value, reads that as "nothing happened", and cancels its own
  // scroll loop after a single frame. Confirmed live: holding a drag right at the viewport edge
  // scrolled the page only once, by a few pixels, and then sat completely still until the mouse
  // moved again, however slightly, for one more small nudge each time, instead of gliding
  // continuously the way it does with smooth-scroll off (verified the same drag scrolls smoothly
  // and continuously once scroll-behavior is set back to auto for the span of the interaction).
  beginInstantScroll() {
    document.documentElement.classList.add("grid-instant-scroll")
  }

  endInstantScroll() {
    document.documentElement.classList.remove("grid-instant-scroll")
  }

  savePositions() {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.persist(), 400)
  }

  async persist() {
    const widgets = [ ...this.element.querySelectorAll(".grid-stack-item") ].map((item) => ({
      id: item.dataset.widgetId,
      grid_x: item.getAttribute("gs-x"),
      grid_y: item.getAttribute("gs-y"),
      grid_w: item.getAttribute("gs-w"),
      grid_h: item.getAttribute("gs-h")
    }))

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    // A refused or failed save (the read-only demo, a dropped request) says so as a toast; the
    // layout stays as GridStack has it on screen, and a reload shows what was actually saved.
    await submitAndReport(this.repositionUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ widgets })
    })
  }
}
